import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import Utils from "../../../../utils/Utils";
import { esEntradaSinPedido } from "../../helpers/entradas";

const textoCheckbox = (fechaRuta) =>
    `Añadir los pedidos a la ruta de Mostrador${
        fechaRuta ? ` con fecha ${Utils.formatDateWithoutHour(new Date(fechaRuta))}` : ""
    }`;

/**
 * Modal de confirmación para finalizar una entrada de cola. Presentacional:
 * `onConfirm(anadirARuta)` lo resuelve la página. El checkbox de alta en ruta
 * solo aparece si la entrada tiene pedido y vuelve a marcarse en cada apertura.
 */
export default function ModalFinalizarEntrada({
    show,
    onHide,
    onConfirm,
    entrada,
    empleadoId,
    fechaRuta = null,
    finalizando = false,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [anadirARuta, setAnadirARuta] = useState(true);

    useEffect(() => {
        if (show) setAnadirARuta(true);
    }, [show]);

    const sinPedido = esEntradaSinPedido(entrada?.numero);
    // Misma regla que el legacy: sin empleado no se puede dar de alta en ruta
    const confirmarDeshabilitado = finalizando || (anadirARuta && !sinPedido && !empleadoId);

    return (
        <Dialog
            open={show}
            onClose={finalizando ? undefined : onHide}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: { borderRadius: isMobile ? 0 : 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", m: isMobile ? 0 : 2 },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <DoneIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: "#212529", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                        {isMobile ? "Finalizar" : "Finalizar entrada"}
                    </Typography>
                </Box>
                <IconButton onClick={onHide} disabled={finalizando} size="small" aria-label="Cerrar">
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, px: { xs: 2, sm: 3 }, flex: isMobile ? 1 : "initial" }}
            >
                <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: "#495057", lineHeight: 1.6, mb: 1.5 }}>
                    {entrada?.numero ? (
                        <>
                            ¿Quieres finalizar el número <strong>{entrada.numero}</strong>?
                        </>
                    ) : (
                        "¿Quieres finalizar esta entrada?"
                    )}
                </Typography>
                {!sinPedido && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={anadirARuta}
                                onChange={(e) => setAnadirARuta(e.target.checked)}
                                disabled={finalizando}
                            />
                        }
                        label={textoCheckbox(fechaRuta)}
                    />
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column-reverse", sm: "row" } }}>
                <Button
                    variant="outlined"
                    onClick={onHide}
                    fullWidth={isMobile}
                    disabled={finalizando}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#6c757d", borderColor: "#6c757d", py: { xs: 1.25, sm: 1 } }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onConfirm(anadirARuta)}
                    fullWidth={isMobile}
                    disabled={confirmarDeshabilitado}
                    startIcon={finalizando ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DoneIcon />}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        py: { xs: 1.25, sm: 1 },
                        backgroundColor: "#cc071e",
                        "&:hover": { backgroundColor: "#a30618" },
                        "&:disabled": { backgroundColor: "#6c757d", color: "#fff" },
                    }}
                >
                    {finalizando ? "Finalizando..." : "Confirmar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
